import { motion } from 'framer-motion';
import { Microscope, Lightbulb, Globe } from 'lucide-react';
import { Section } from '@components/ui/Section.jsx';
import { OBJECTIVES } from '@/data/homeContent.js';
import { OptimizedImage } from '@components/ui/OptimizedImage.jsx';
import { cn } from '@/lib/cn.js';

const CARDS = [
  {
    icon: Microscope,
    headline: 'Stimulate Cutting-Edge Research',
    image: '/gallery/2025/home/11.webp',
    number: '01',
  },
  {
    icon: Lightbulb,
    headline: 'Drive Innovation & Governance',
    image: '/assets/images/slider/benner02.webp',
    number: '02',
  },
  {
    icon: Globe,
    headline: 'Unite A Global Community',
    image: '/gallery/2024/home/0.webp',
    number: '03',
  },
];

export function ObjectivesSection() {
  return (
    <Section className="border-t border-ink-200/50 dark:border-ink-800/50 bg-ink-50/50 dark:bg-ink-900/20">

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="text-center mb-16"
      >
        <p className="eyebrow mb-4">What We Stand For</p>
        <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl text-ink-900 dark:text-ink-50 capitalize text-balance">
          {OBJECTIVES.title}
        </h2>
      </motion.div>

      {/* Cards */}
      <div className="grid md:grid-cols-3 gap-6 lg:gap-7 items-start">
        {CARDS.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={i}
              tabIndex={0}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ delay: i * 0.12, duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
              className="group relative flex flex-col self-start w-full rounded-3xl overflow-hidden
                         ring-1 ring-ink-200/70 dark:ring-ink-700/50
                         shadow-soft hover:shadow-glow focus-visible:shadow-glow
                         transition-all duration-500 hover:-translate-y-2 focus-visible:-translate-y-2
                         bg-white dark:bg-ink-900/80 outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60"
            >
              {/* ── Image area ── */}
              <div className="relative overflow-hidden h-52">
                <OptimizedImage
                  src={card.image}
                  alt={card.headline}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                  deferUntilVisible
                />
                {/* Dark gradient that bleeds into the card body */}
                <div className="absolute inset-0 bg-gradient-to-b from-ink-950/20 via-ink-950/30 to-ink-950/80" />

                {/* Large decorative number watermark */}
                <span
                  aria-hidden
                  className="absolute -right-2 top-2 font-display font-black text-[7rem] leading-none
                             text-white/22 group-hover:text-white/32
                             drop-shadow-[0_2px_16px_rgba(255,255,255,0.2)]
                             select-none pointer-events-none transition-colors duration-500"
                >
                  {card.number}
                </span>

              </div>

              {/* ── Icon bridge — sits on the image/text boundary ── */}
              <div className="relative -mt-5 mx-5 z-10 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl
                                bg-brand-600 dark:bg-brand-500
                                flex items-center justify-center shadow-md
                                transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-3">
                  <Icon size={18} className="text-white" strokeWidth={1.8} />
                </div>
                {/* thin accent line extending from icon */}
                <div className="flex-1 h-px bg-gradient-to-r from-brand-500/60 via-brand-300/30 to-transparent" />
              </div>

              {/* ── Text body ── */}
              <div className="flex flex-col gap-3 px-6 pt-4 pb-7">
                <h3 className="font-display text-lg font-semibold leading-snug
                               text-ink-900 dark:text-ink-50 group-hover:text-brand-600
                               dark:group-hover:text-brand-300 transition-colors duration-300">
                  {card.headline}
                </h3>
                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-500 ease-out',
                    'grid-rows-[0fr] group-hover:grid-rows-[1fr] group-focus-visible:grid-rows-[1fr]',
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="text-sm text-ink-600 dark:text-ink-300 leading-relaxed pt-1">
                      {OBJECTIVES.items[i]}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── Bottom accent bar that slides in on hover ── */}
              <div className="h-[3px] w-full bg-gradient-to-r from-brand-500 via-accent-400 to-brand-300
                              translate-y-[3px] group-hover:translate-y-0
                              transition-transform duration-500 ease-out" />
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}
